import {gql} from '@apollo/client';
import {BULK_SWAP_JOB_FIELDS} from 'graphql/mutations/wallet';

export const GET_ALL_WALLET_PAYMENT_PROVIDERS = gql`
  query GetAllPaymentProviders {
    getAllPaymentProviders {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on PaymentProviderList {
        providers {
          id
          enabled
          provider
          currencies
        }
      }
    }
    getPlatformSupportedCurrenciesForAdmin {
      ... on Error {
        message
        statusCode
      }
      ... on PlatformSupportedCurrencyList {
        currencies {
          enabled
          currency
          currencySymbol
          swap {
            sourceCurrencyEnabledForSwap
            minSwapAmountInSourceCurrency
            maxSwapAmountInSourceCurrency
          }
          currencyName
          deposit {
            enabledPaymentMethods
            paymentMethodConfigs {
              method
              enabled
              available
              order
              logoUrl
              restrictedCountries
              displayName {
                language
                text
              }
              subText {
                language
                text
              }
              feeText {
                language
                text
              }
            }
          }
          withdrawal {
            enabledPaymentMethods
            paymentMethodConfigs {
              method
              enabled
              available
              order
              logoUrl
              restrictedCountries
              displayName {
                language
                text
              }
              subText {
                language
                text
              }
              feeText {
                language
                text
              }
            }
          }
        }
      }
    }
    getMobileMoneyOperatorsForAdmin {
      ... on Error {
        message
        statusCode
      }
      ... on MobileMoneyOperatorsList {
        operators {
          countryName
          currencyCode
          logo
          supportedProviders {
            providerCode
          }
        }
      }
    }
  }
`;

export const GET_BOTH_PLATFORM_AND_WALLET_CURRENCY = gql`
  query GetPaymentProviderByProviderName(
    $providerName: PaymentGatewayProviderTypes!
    $currency: WalletCurrency
    $currency2: WalletCurrency!
  ) {
    getPaymentProviderByProviderName(
      providerName: $providerName
      currency: $currency
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on PaymentProviderSettings {
        currencies
        enabled
        provider
      }
    }
    getSupportedCurrencyByCurrencyName(currency: $currency2) {
      ... on Error {
        message
        statusCode
      }
      ... on WalletCurrencySettings {
        deposit {
          limit {
            daily
            monthly
            weekly
          }
        }
        withdrawal {
          limit {
            daily
            weekly
            monthly
          }
        }
      }
    }
  }
`;

export const GET_PLATFORM_WALLET_CURRENCY_BY_NAME = gql`
  query GetSupportedCurrencyByCurrencyName($currency: WalletCurrency!) {
    getSupportedCurrencyByCurrencyName(currency: $currency) {
      ... on Error {
        message
        statusCode
      }
      ... on WalletCurrencySettings {
        enabled
        currency
        currencySymbol
        currencyName
        displayCurrency {
          enabled
        }
        deposit {
          enabledPaymentMethods
          limit {
            daily
            monthly
            weekly
          }
          enabled
          paymentMethodConfigs {
            method
            enabled
            available
            order
            logoUrl
            restrictedCountries
            displayName {
              language
              text
            }
            subText {
              language
              text
            }
            feeText {
              language
              text
            }
          }
        }
        withdrawal {
          enabledPaymentMethods
          enabled
          limit {
            daily
            weekly
            monthly
          }
          paymentMethodConfigs {
            method
            enabled
            available
            order
            logoUrl
            restrictedCountries
            displayName {
              language
              text
            }
            subText {
              language
              text
            }
            feeText {
              language
              text
            }
          }
        }
      }
    }
  }
`;

export const GET_ALL_WALLET_TRANSACTIONS_FOR_ADMIN = gql`
  query GetAllTransactionsForAdmin($input: GetWalletTransactionInput!) {
    getAllTransactionsForAdmin(input: $input) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on WalletTransactionList {
        page
        totalDocs
        totalPages
        hasPrevPage
        hasNextPage
        prevPage
        nextPage

        transactions {
          createdAt
          currency
          locked
          direction
          id
          referenceId
          failureReasonCode
          failureReasonMessages
          paymentMetadata
          paymentGateway
          claimedTransaction {
            id
            createdAt
            createdBy {
              email
              firstName
              lastName
            }
          }
          paymentProvider
          type
          status
          providerStatus
          providerTransferId
          totalTransactionAmountIncludingFees
          transactionAmountNetOfFees
          transactionFee
          updatedAt
          user {
            id
            email
            firstName
            lastName
            username
          }
          order {
            totalFee
            type
            price
            direction
            assetCurrency
            symbol
          }
          swap {
            # id and referenceId are what tie the two legs of a swap
            # together: the source ledger's WITHDRAWAL and the target
            # ledger's DEPOSIT carry the same pair, while their own
            # transaction referenceIds differ.
            id
            referenceId
            rate
            calculatedFees
            amountSwapped
            amountReceived
            sourceCurrency {
              currency
            }
            targetCurrency {
              currency
            }
          }
        }
      }
    }
  }
`;

export const GET_MOBILE_MONEY_OPERATOR_BY_NAME = gql`
  query GetMobileMoneyOperatorByCountry(
    $countryName: MobileSupportedCountries!
  ) {
    getMobileMoneyOperatorByCountry(countryName: $countryName) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on MobileMoneyOperators {
        countryName
        currencyCode
        countryISO
        provider
        logo
        subText
        supportedProviders {
          providerName
          cashoutServiceId
          cashinServiceId
          gateway
          isActive
          howToGuide {
            description
            imgURL
            videoURL
            link
          }
          direction
          feeSchedule {
            deposit {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
            withdrawal {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
          }
          countryCode
          feeSchedule {
            deposit {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
            withdrawal {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
          }
          discount {
            deposit {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
            withdrawal {
              fixedFeeAmount
              variableFeeAmount
              feeType
            }
          }
          providerLogo
          minWithdrawalAmount
          maxWithdrawalAmount
          minDepositAmount
          maxDepositAmount
          mobileMoneyDepositUSSDCode
          mobileMoneyDepositUSSDCodeDescription
          mobileMoneyWithdrawalUSSDCode
          mobileMoneyWithdrawalUSSDCodeDescription
          providerCode
        }
      }
    }
  }
`;

export const GET_GLOBAL_WALLET_PROVIDERS = gql`
  query GlobalPlatformWalletSettings {
    getGlobalPlatformSettings {
      ... on GlobalPlatformWalletSettings {
        isDepositAllowed
        isTransfersAllowed
        isWithdrawalAllowed
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const GET_WALLET_TRANSACTION_BY_ID = gql`
  query AdminGetWalletTransactionById(
    $transactionId: ID!
    $currency: WalletCurrency!
    $userId: ID!
  ) {
    adminGetWalletTransactionById(
      transactionId: $transactionId
      currency: $currency
      userId: $userId
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on WalletTransaction {
        id
        currency
        transactionAmountNetOfFees
        locked
        totalTransactionAmountIncludingFees
        transactionFee
        paymentGateway
        referenceId
        failureReasonCode
        failureReasonMessages
        paymentGateway
        type
        status
        providerStatus
        providerTransferId
        user {
          imageUrl
          firstName
          id
          username
          lastName
        }
        createdAt
        updatedAt
        order {
          totalFee
          type
          price
          direction
          assetCurrency
          symbol
        }
        swap {
          id
          referenceId
          status
          createdAt
          calculatedFees
          actualFeeWithoutDiscount
          amountSavedInFees
          isUserDabaPro
          rate
          rateUsed
          configuredPairRate
          currentMarketRate
          adminEnteredRate
          usedCustomExchangeRate
          amountSwapped
          amountReceived
          amountPaid
          sourceCurrency {
            currency
            currencySymbol
          }
          targetCurrency {
            currency
            currencySymbol
          }
        }
      }
    }
  }
`;

export const GET_PAYMENT_PROVIDER_DETAILS_BY_ID = gql`
  query GetPaymentProviderByProviderName(
    $providerName: PaymentGatewayProviderTypes!
    $currency: WalletCurrency
  ) {
    getPaymentProviderByProviderName(
      providerName: $providerName
      currency: $currency
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on PaymentProviderSettings {
        currencies
        enabled
        provider
      }
    }
  }
`;

export const GET_MOBILE_MONEY_OPERATOR = gql`
  query GetMobileMoneyOperatorsForAdmin {
    getMobileMoneyOperatorsForAdmin {
      ... on Error {
        message
        statusCode
      }
      ... on MobileMoneyOperatorsList {
        operators {
          provider
          name
          code
          supportedCurrencies {
            name
          }
        }
      }
    }
  }
`;

// FX RATES

export const GET_ALL_FX_EXCHANGE_RATES = gql`
  query GetAllFxExchangeRates {
    getAllFxExchangeRates {
      ... on Error {
        message
        statusCode
      }
      ... on ExchangeRateList {
        rates {
          id
          enabled
          rate
          sourceCurrency
          targetCurrency
          shouldCheckMarketRate
          requireDepositForTargetCurrencySwap
          requirePriorSwapFromTargetCurrency
          dailyThresholdForAutomaticSwapToTargetCurrency {
            initial
            remaining
            shouldNotifyAdminIfRemainingIsBelow
          }
        }
      }
    }
  }
`;

export const GET_TOTAL_USER_CASH_BALANCE = gql`
  query GetPlatformUserTotalCashBalance($input: GetUserTotalCashBalanceInput) {
    getPlatformUserTotalCashBalance(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on PlatformUserTotalCashBalance {
        currency {
          display
          source
        }
        totalBalanceInSourceCurrency
        totalBalanceInDisplayCurrency
        paymentProviderBalance
      }
    }
  }
`;

export const GET_PUBLIC_EXCHANGE_RATES = gql`
  query GetPublicFxExchangeRates(
    $from: WalletCurrency!
    $to: [WalletCurrency]
  ) {
    getPublicFxExchangeRates(from: $from, to: $to) {
      ... on Error {
        message
        statusCode
      }
      ... on PublicExchangeRate {
        from
        to {
          rate
          currency
        }
        lastUpdated
      }
    }
  }
`;

export const GET_FX_EXCHANGE_RATE_BY_ID = gql`
  query GetFxExchangeRateById($getFxExchangeRateByIdId: ID!) {
    getFxExchangeRateById(id: $getFxExchangeRateByIdId) {
      ... on Error {
        message
        statusCode
      }
      ... on ExchangeRate {
        enabled
        feeSchedule {
          feeType
          fixedFeeAmount
          variableFeeAmount
        }
        discount {
          feeType
          fixedFeeAmount
          variableFeeAmount
        }
        id
        minSwapAmountInSourceCurrency
        maxSwapAmountInSourceCurrency
        rate
        shouldCheckMarketRate
        requireDepositForTargetCurrencySwap
        requirePriorSwapFromTargetCurrency
        sourceCurrency
        targetCurrency
        tieredDiscountSchedule {
          applyTo
          minimumAmountThreshold
          discountType
          fixedFeeAmount
          variableFeeAmount
        }
      }
    }
  }
`;

export const GET_ALL_SWAP_TRANSACTIONS = gql`
  query GetAllSwapTransactions($input: SwapFilters) {
    getAllSwapTransactions(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on AllSwapTransactionsList {
        totalSwapTransactions
        swapTransactions {
          sourceCurrency {
            currency
          }
          targetCurrency {
            currency
          }
          rate
          amountPaid
          amountSwapped
          amountReceived
          metadata
          calculatedFees
          status
          createdAt
          id
          lockId
          referenceId
          user {
            imageUrl
            firstName
            lastName
            email
            id
          }
        }
        paginate {
          hasNextPage
          hasPrevPage
          total
        }
      }
    }
  }
`;

// One response for all four status cards. Ignores `input.status` on purpose,
// so every card stays filled while one status tab is selected.
const SWAP_STATUS_SUMMARY = `
  count
  normalizedUsd
  sourceTotals {
    currency
    amount
  }
  targetTotals {
    currency
    amount
  }
`;

export const GET_SWAP_TRANSACTION_SUMMARY = gql`
  query GetSwapTransactionSummary($input: SwapFilters) {
    getSwapTransactionSummary(input: $input) {
      __typename
      ... on SwapTransactionSummary {
        all { ${SWAP_STATUS_SUMMARY} }
        pending { ${SWAP_STATUS_SUMMARY} }
        successful { ${SWAP_STATUS_SUMMARY} }
        failed { ${SWAP_STATUS_SUMMARY} }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

// One bulk fulfil/reject job, readable only by the admin who started it.
export const GET_BULK_SWAP_JOB = gql`
  query SwapBulkJobProgress($jobId: ID!) {
    getBulkSwapTransactionJob(jobId: $jobId) {
      __typename
      ... on BulkSwapTransactionJob {
        ${BULK_SWAP_JOB_FIELDS}
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

// The signed-in admin's recent bulk jobs, newest first.
export const GET_MY_BULK_SWAP_JOBS = gql`
  query MyRecentSwapBulkJobs($limit: Int) {
    getMyBulkSwapTransactionJobs(limit: $limit) {
      ${BULK_SWAP_JOB_FIELDS}
    }
  }
`;

export const GENERATE_TRANSACTION_REPORT = gql`
  query GenerateTransactionReport($filters: TransactionReportFilters) {
    generateTransactionReport(filters: $filters) {
      ... on Error {
        message
        statusCode
      }
      ... on TransactionReport {
        responseMessage
      }
    }
  }
`;

export const GENERATE_CURRENCY_EXPOSURE = gql`
  query Error {
    generateCurrencyExposureReport {
      ... on Error {
        message
        statusCode
      }
      ... on TransactionReport {
        responseMessage
      }
    }
  }
`;

export const GENERATE_RECONCILIATION_REPORT = gql`
  query GenerateReconciliationReport($filters: TransactionReportFilters) {
    generateReconciliationReport(filters: $filters) {
      ... on Error {
        message
        statusCode
      }
      ... on TransactionReport {
        responseMessage
      }
    }
  }
`;

export const GENERATE_CUSTOMER_ACTIVITY = gql`
  query GenerateCustomerActivityReport($filters: TransactionReportFilters) {
    generateCustomerActivityReport(filters: $filters) {
      ... on Error {
        message
        statusCode
      }
      ... on TransactionReport {
        responseMessage
      }
    }
  }
`;

export const GENERATE_LIQUIDITY_REPORT = gql`
  query Error {
    generateLiquidityReport {
      ... on Error {
        message
        statusCode
      }
      ... on TransactionReport {
        responseMessage
      }
    }
  }
`;

export const CLAIM_TRANSACTION = gql`
  mutation ClaimTransaction($transactionId: ID!, $currency: WalletCurrency!) {
    claimTransaction(transactionId: $transactionId, currency: $currency) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWalletTransaction {
        data {
          id
        }
      }
    }
  }
`;

export const UNCLAIM_TRANSACTION = gql`
  mutation RemoveClaimedTransaction(
    $transactionId: ID!
    $currency: WalletCurrency!
  ) {
    removeClaimedTransaction(
      transactionId: $transactionId
      currency: $currency
    ) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWalletTransaction {
        data {
          id
        }
      }
    }
  }
`;

export const DEPOSIT_SIM_WALLET = gql`
  mutation CreditSimWallet($input: CreditSimWalletInput!) {
    creditSimWallet(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithManualCreateWalletTransaction {
        message
      }
    }
  }
`;

export const GET_SIM_WALLET_TRANSACTIONS = gql`
  query GetSimWalletTransactions($input: GetWalletTransactionInput!) {
    getSimWalletTransactions(input: $input) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on WalletTransactionList {
        page
        totalDocs
        totalPages
        hasPrevPage
        hasNextPage
        prevPage
        nextPage

        transactions {
          createdAt
          currency
          locked
          direction
          id
          referenceId
          failureReasonCode
          failureReasonMessages
          paymentMetadata
          paymentGateway
          claimedTransaction {
            id
            createdAt
            createdBy {
              email
              firstName
              lastName
            }
          }
          paymentProvider
          type
          status
          totalTransactionAmountIncludingFees
          transactionAmountNetOfFees
          transactionFee
          updatedAt
          user {
            id
            firstName
            lastName
            username
          }
          order {
            totalFee
            type
            price
            direction
          }
          swap {
            calculatedFees
            amountSwapped
            amountReceived
            sourceCurrency {
              currency
            }
            targetCurrency {
              currency
            }
          }
        }
      }
    }
  }
`;

export const GET_USER_SIM_WALLETS = gql`
  query AdminGetUserSimWalletList($input: WalletListInput) {
    adminGetUserSimWalletList(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on UserWalletList {
        totalBalanceInDisplayCurrency
        wallets {
          id
          currency
          currencySymbol
          currencyName
          status
          colorCode
          lockedCardDepositAmount
          totalDeposits
          balance {
            availableBalance
            availableForInvesting
            lockedBalance
            ledgerBalance
            pendingDepositBalance
            pendingWithdrawalBalance
          }
          accountFlags {
            by {
              firstName
              lastName
            }
            isFlagged
            notes
            reason
          }
          user {
            firstName
            lastName
            id
            email
          }
        }
      }
    }
  }
`;

export const GET_WALLET_TRANSACTIONS_BY_WISE_TRANSFER_ID = gql`
  query GetWalletTransactionsByWiseTransferId($transferId: String!) {
    getWalletTransactionsByWiseTransferId(transferId: $transferId) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on WalletTransactionsByWiseTransferIdResult {
        transferId
        count
        resultMessage: message
        hits {
          source
          transaction {
            id
            currency
            createdAt
            updatedAt
            status
            direction
            type
            referenceId
            paymentProvider
            paymentGateway
            paymentMetadata
            failureReasonCode
            failureReasonMessages
            transactionAmountNetOfFees
            totalTransactionAmountIncludingFees
            transactionFee
            user {
              id
              email
              firstName
              lastName
              username
            }
          }
        }
      }
    }
  }
`;

// Counts and totals per status for the same input as
// GET_ALL_WALLET_TRANSACTIONS_FOR_ADMIN. Ignores status, pagination and export;
// with no startDate it covers the 366 days before endDate (or now).
const WALLET_SUMMARY_BUCKET = gql`
  fragment WalletSummaryBucket on WalletTransactionStatusSummary {
    count
    amount
    net
    fees
    normalizedUsd
  }
`;

export const GET_WALLET_TRANSACTION_SUMMARY = gql`
  ${WALLET_SUMMARY_BUCKET}
  query GetWalletTransactionSummary($input: GetWalletTransactionInput!) {
    getWalletTransactionSummary(input: $input) {
      __typename
      ... on WalletTransactionSummary {
        currency
        startDate
        endDate
        all {
          ...WalletSummaryBucket
        }
        pending {
          ...WalletSummaryBucket
        }
        successful {
          ...WalletSummaryBucket
        }
        failed {
          ...WalletSummaryBucket
        }
        cancelled {
          ...WalletSummaryBucket
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;
